import { isValid } from "date-fns";
import {
  model,
  Model,
  prop,
  idProp,
  rootRef,
  detach,
  modelAction,
  findParent,
  modelFlow,
  _async,
} from "mobx-keystone";
import { isValidMainstreamBitcoinAddress } from "../helpers/sendOnchain";
import { Bob } from "./Bob";
import { sendToBobController } from "../../helpers/requests";
import { notifications } from "@mantine/notifications";

@model("exrails/OnchainPaymentProposal")
export class OnchainPaymentProposal extends Model({
  _version: prop(0),
  id: idProp,
  proposedAddress: prop<string | null>(null),
  userFacingAddressErrorMessage: prop<string | null>(null),
  showProposedAmountSatoshisField: prop<boolean>(false),
  proposedAmountSatoshis: prop<number | null>(null),
  proposedAmountSatoshisIsValid: prop<boolean>(false),
  userFacingProposedAmountErrorMessage: prop<string | null>(null),
  validProposedAddress: prop<string | null>(null),
  platformFeeSatoshisFromServer: prop<number | null>(null),
  // the two parts of platformFeeSatoshisFromServer, shown on separate lines
  platformCutSatoshisFromServer: prop<number | null>(null),
  networkFeeSatoshisFromServer: prop<number | null>(null),
  platformFeePercentageFromServer: prop<number | null>(null),
  uuidFromServer: prop<string | null>(null),
  userProvided2FaCode: prop<string | null>(null),
  confirmedTransactionIdFromServer: prop<string | null>(null),
}) {
  @modelFlow
  sendOnchainPayment = _async(function* (this: OnchainPaymentProposal) {
    this.bob.setAreWeWaitingOnRails(true);
    const message = {
      bobUuid: this.bob.uuid,
      onchainOutboundTransactionQuoteUuid: this.uuidFromServer!,
      twoFactorCode: this.userProvided2FaCode,
    };
    sendToBobController(message, "/bob_life/send_onchain_payment")
      .then((response) => {
        this.bob.setAreWeWaitingOnRails(false);
        if (response.success == false) {
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          return;
        }

        console.log("onchain outbound transaction sent", response);

        // Set the transaction ID from the most recent transaction in the array
        if (
          response.onchainTransactions &&
          Array.isArray(response.onchainTransactions) &&
          response.onchainTransactions.length > 0
        ) {
          const mostRecent = response.onchainTransactions[0];
          if (mostRecent.transaction_identifier) {
            this.setConfirmedTransactionId(mostRecent.transaction_identifier);
          }
          this.bob.setOurOnchainOutboundTransactions(
            response.onchainTransactions
          );
          this.bob.workspace.makeEvent(
            "Onchain Payment Sent",
            `An onchain payment of ${mostRecent.amount_satoshis.toLocaleString()} satoshis has been broadcast to ${mostRecent.destination_address.slice(
              0,
              10
            )}...`,
            "onchain-payment-sent",
            null,
            mostRecent.uuid
          );
        }
      })
      .catch((error) => {
        this.bob.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  @modelFlow
  makeNewOnchainOutboundTransactionQuote = _async(function* (
    this: OnchainPaymentProposal
  ) {
    console.log(
      "making new onchain outbound transaction quote",
      this.validProposedAddress,
      this.proposedAmountSatoshis
    );
    this.bob.setAreWeWaitingOnRails(true);
    this.bob.checkIfWeNeedToShowTwoFactorForm();
    const message = {
      bobUuid: this.bob.uuid,
      destinationAddress: this.validProposedAddress!,
      amountInSatoshis: this.proposedAmountSatoshis!,
    };
    sendToBobController(
      message,
      "/bob_life/maybe_make_bob_onchain_outbound_transaction_quote"
    )
      .then((response) => {
        console.log("response from quote request", response);
        const quoteData = response.quoteData;

        if (response.success == false) {
          this.bob.setAreWeWaitingOnRails(false);
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          return;
        }
        if (quoteData.amountInSatoshis !== this.proposedAmountSatoshis) {
          notifications.show({
            title: "Error",
            message:
              "The amount returned by the server does not match the proposed amount. Please try again.",
            color: "red",
          });
          return;
        }
        if (quoteData.destinationAddress !== this.validProposedAddress) {
          notifications.show({
            title: "Error",
            message:
              "The address returned by the server does not match the proposed address. Please try again.",
            color: "red",
          });
          return;
        }

        console.log("onchain outbound transaction quote created", response);
        // this.platformFeeSatoshisFromServer = quoteData.platformFeesSatoshis;
        // this.uuidFromServer = quoteData.uuid;
        this.setConfirmedQuote(quoteData);
      })
      .catch((error) => {
        this.bob.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  @modelAction
  setConfirmedQuote(quoteData: {
    platformFeesSatoshis: number;
    platformCutSatoshis?: number;
    networkFeeSatoshis?: number;
    platformFeePercentage?: number;
    uuid: string;
  }) {
    this.platformFeeSatoshisFromServer = quoteData.platformFeesSatoshis;
    this.platformCutSatoshisFromServer = quoteData.platformCutSatoshis ?? null;
    this.networkFeeSatoshisFromServer = quoteData.networkFeeSatoshis ?? null;
    this.platformFeePercentageFromServer =
      quoteData.platformFeePercentage ?? null;
    this.uuidFromServer = quoteData.uuid;
    this.bob.setAreWeWaitingOnRails(false);
  }

  @modelAction
  setConfirmedTransactionId(transactionId: string) {
    this.confirmedTransactionIdFromServer = transactionId;
  }

  @modelAction
  setUserProvided2FaCode(code: string | null) {
    this.userProvided2FaCode = code;
  }

  @modelAction
  setProposedAddress(address: string) {
    this.proposedAddress = address;
    if (isValidMainstreamBitcoinAddress(address)) {
      this.userFacingAddressErrorMessage = null;
      this.validProposedAddress = address;
    } else {
      this.userFacingAddressErrorMessage =
        "This does not appear to be a valid Bitcoin address.";
      this.validProposedAddress = null;
    }
  }

  @modelAction
  setProposedAmountSatoshis(amount: number | null) {
    this.proposedAmountSatoshis = amount;

    if (amount === null || amount === undefined) {
      this.proposedAmountSatoshisIsValid = false;
      this.userFacingProposedAmountErrorMessage = null;
      return;
    }

    if (!Number.isFinite(amount)) {
      this.proposedAmountSatoshisIsValid = false;
      this.userFacingProposedAmountErrorMessage = "Please enter a valid number.";
      return;
    }

    if (amount < 0) {
      this.proposedAmountSatoshisIsValid = false;
      this.userFacingProposedAmountErrorMessage = "Amount cannot be negative.";
      return;
    }

    if (!Number.isInteger(amount)) {
      this.proposedAmountSatoshisIsValid = false;
      this.userFacingProposedAmountErrorMessage =
        "Amount must be a whole number of satoshis (no decimals).";
      return;
    }

    if (amount < this.bob.workspace.minOnchainPaymentSizeSatoshis!) {
      this.proposedAmountSatoshisIsValid = false;
      this.userFacingProposedAmountErrorMessage = `Amount must be at least ${this.bob.workspace.minOnchainPaymentSizeSatoshis!.toLocaleString()} sats.`;
      return;
    }

    const maxAmount = this.maxSendableSatoshis;
    if (amount > maxAmount) {
      this.proposedAmountSatoshisIsValid = false;
      this.userFacingProposedAmountErrorMessage =
        maxAmount > 0
          ? `Amount is too high. After the platform fee and network fee, the most you can send is ${maxAmount.toLocaleString()} sats.`
          : `Your balance of ${this.bob.safeBalanceInSatoshis.toLocaleString()} sats is not enough to cover an on-chain payment plus fees.`;
      return;
    }

    this.proposedAmountSatoshisIsValid = true;
    this.userFacingProposedAmountErrorMessage = null;
  }

  // Max amount accounts for the platform fee % on top + ~500 sats network fee
  // Total cost = amount * (1 + pct) + 500, so max = (balance - 500) / (1 + pct)
  get maxSendableSatoshis(): number {
    const txFee = 500;
    const feeMultiplier =
      1 + this.bob.workspace.weKeepThisPlatformFeePercentageForOnchainPayments!;
    return Math.floor((this.bob.safeBalanceInSatoshis - txFee) / feeMultiplier);
  }

  get bob() {
    return findParent<Bob>(this, (parentNode) => parentNode instanceof Bob);
  }
}

export interface OnchainPaymentProposalInterface {
  OnchainPaymentProposal: OnchainPaymentProposal;
}

export const OnchainPaymentProposalRef = rootRef<OnchainPaymentProposal>(
  "exrails/OnchainPaymentProposalRef",
  {
    onResolvedValueChange(ref, newThing, oldThing) {
      if (newThing && !oldThing) {
        detach(ref);
      }
    },
  }
);
